import { getNavItemsApi } from '@/api-calls/nav-items/get'
import {
  deleteNavItemApi,
  updateNavItemApi,
} from '@/api-calls/nav-items/mutate'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import { Button, Skeleton } from '@blogging/ui'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Menu,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'

interface NavItem {
  id: string
  title: string
  url: string | null
  group: 'header' | 'footer'
  order: number
  status: 'draft' | 'published'
  parentId: string | null
}

interface NavItemRowProps {
  item: NavItem
  siteId: string
  onDelete: (id: string) => void
  onMove: (item: NavItem, direction: 'up' | 'down') => void
  canMoveUp: boolean
  canMoveDown: boolean
  isDeleting: boolean
  isMoving: boolean
  indent?: boolean
}

function NavItemRow({
  item,
  siteId,
  onDelete,
  onMove,
  canMoveUp,
  canMoveDown,
  isDeleting,
  isMoving,
  indent = false,
}: NavItemRowProps) {
  return (
    <div
      className={`flex items-center justify-between p-3 border rounded-lg bg-background ${
        indent ? 'ml-8 border-dashed' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        {indent && (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
        <div>
          <p className="font-medium">{item.title}</p>
          {item.url && (
            <p className="text-sm text-muted-foreground">{item.url}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span
          className={`text-xs px-2 py-0.5 rounded ${
            item.status === 'published'
              ? 'bg-green-100 text-green-700'
              : 'bg-yellow-100 text-yellow-700'
          }`}
        >
          {item.status}
        </span>
        <Button
          variant="ghost"
          size="sm"
          title="Move up"
          onClick={() => onMove(item, 'up')}
          disabled={!canMoveUp || isMoving}
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          title="Move down"
          onClick={() => onMove(item, 'down')}
          disabled={!canMoveDown || isMoving}
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Link to={`/${siteId}/settings/nav/${item.id}`}>
          <Button variant="ghost" size="sm">
            <Pencil className="h-4 w-4" />
          </Button>
        </Link>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(item.id)}
          disabled={isDeleting}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export function SiteSettingsNavPage() {
  const { siteId } = useParams()
  const queryClient = useQueryClient()

  const {
    data: navItems,
    isLoading,
    error,
  } = useFetch(getNavItemsApi, {
    resourceName: 'navItems',
    initialParams: { siteId: siteId as string },
  })

  const { mutate: updateNavItem, isPending: isMoving } = useMutation(
    updateNavItemApi,
    {
      resourceName: 'navItem',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getNavItemsApi.apiName, { siteId }],
        })
      },
      onError: (error: any) => {
        toast.error(
          error?.data?.error ||
            error?.message ||
            'Failed to reorder navigation item',
        )
      },
    },
  )

  const { mutate: deleteNavItem, isPending: isDeleting } = useMutation(
    deleteNavItemApi,
    {
      resourceName: 'navItem',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getNavItemsApi.apiName, { siteId }],
        })
        toast.success('Navigation item deleted!')
      },
      onError: (error: any) => {
        toast.error(
          error?.data?.error ||
            error?.message ||
            'Failed to delete navigation item',
        )
      },
    },
  )

  const handleDelete = (id: string) => {
    if (confirm('Are you sure? Child items will also be deleted.')) {
      deleteNavItem({ id, siteId })
    }
  }

  const all: NavItem[] = navItems || []
  const topLevel = (group: 'header' | 'footer') =>
    all
      .filter((i) => i.group === group && !i.parentId)
      .sort((a, b) => a.order - b.order)
  const childrenOf = (id: string) =>
    all.filter((i) => i.parentId === id).sort((a, b) => a.order - b.order)

  // Swap the item with its neighbor by renumbering the whole sibling list,
  // since existing items may share the same order value.
  const handleMove = (item: NavItem, direction: 'up' | 'down') => {
    const siblings = item.parentId
      ? childrenOf(item.parentId)
      : topLevel(item.group)
    const index = siblings.findIndex((i) => i.id === item.id)
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= siblings.length) return

    const reordered = [...siblings]
    reordered[index] = siblings[targetIndex]
    reordered[targetIndex] = siblings[index]
    reordered.forEach((sibling, i) => {
      if (sibling.order !== i) {
        updateNavItem({ id: sibling.id, siteId, order: i })
      }
    })
  }

  const NavSection = ({
    group,
    label,
  }: {
    group: 'header' | 'footer'
    label: string
  }) => {
    const items = topLevel(group)
    return (
      <div className="space-y-3">
        <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
          {label}
        </h4>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No {label.toLowerCase()} items yet.
          </p>
        ) : (
          <div className="space-y-2">
            {items.map((item, index) => {
              const children = childrenOf(item.id)
              return (
                <div key={item.id}>
                  <NavItemRow
                    item={item}
                    siteId={siteId as string}
                    onDelete={handleDelete}
                    onMove={handleMove}
                    canMoveUp={index > 0}
                    canMoveDown={index < items.length - 1}
                    isDeleting={isDeleting}
                    isMoving={isMoving}
                  />
                  {children.length > 0 && (
                    <div className="mt-1.5 space-y-1.5">
                      {children.map((child, childIndex) => (
                        <NavItemRow
                          key={child.id}
                          item={child}
                          siteId={siteId as string}
                          onDelete={handleDelete}
                          onMove={handleMove}
                          canMoveUp={childIndex > 0}
                          canMoveDown={childIndex < children.length - 1}
                          isDeleting={isDeleting}
                          isMoving={isMoving}
                          indent
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <Skeleton className="h-48 w-full rounded-lg" />
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex flex-col space-y-2">
            <h1 className="text-3xl font-bold">Navigation</h1>
            <p className="text-muted-foreground">
              Configure your site's navigation menus.
            </p>
          </div>
          <Link to={`/${siteId}/settings/nav/new`}>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Item
            </Button>
          </Link>
        </div>

        <div className="rounded-lg border bg-card p-6 space-y-8">
          {error ? (
            <div className="text-destructive">
              Failed to load navigation items
            </div>
          ) : all.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Menu className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No navigation items yet.</p>
              <p className="text-sm mb-4">
                Add items to build your site's menu.
              </p>
              <Link to={`/${siteId}/settings/nav/new`}>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  New Item
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-8">
              <NavSection group="header" label="Header Navigation" />
              <NavSection group="footer" label="Footer Navigation" />
            </div>
          )}
        </div>
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}
